/**
 * TypeScript API client for User Preferences
 * Provides type-safe access to user preference endpoints
 */

import { getCsrfToken } from "bundles/utilities";
import { fetchWithNetworkRetry } from "utilities/fetchWithNetworkRetry";

// Type definitions
export interface PreferenceValue {
  [key: string]: unknown;
}

export interface PreferencesResponse {
  preferences: Record<string, PreferenceValue>;
}

export interface PreferenceResponse {
  namespace: string;
  value: PreferenceValue;
}

export interface DefaultsResponse {
  defaults: Record<string, PreferenceValue>;
}

export interface SchemasResponse {
  schemas: Record<string, JSONSchema>;
}

export interface JSONSchema {
  type: string;
  properties?: Record<string, unknown>;
  required?: string[];
  additionalProperties?: boolean;
  [key: string]: unknown;
}

export interface ErrorResponse {
  error: string;
}

/**
 * User Preferences API Client
 */
export class UserPreferencesApi {
  private baseUrl: string;

  constructor(baseUrl: string = "/api/v1/user_preferences") {
    this.baseUrl = baseUrl;
  }

  /**
   * Build headers with CSRF token
   */
  private buildHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      headers["X-CSRF-Token"] = csrfToken;
    }
    return headers;
  }

  /**
   * Handle response errors and parse JSON body.
   * Returns parsed JSON, eliminating double-read risk.
   */
  private async handleResponse<T>(
    response: Response,
    action: string
  ): Promise<T> {
    if (response.status === 401) {
      window.dispatchEvent(new CustomEvent("auth:session-expired"));
      throw new Error("Session expired. Please log in again.");
    }
    if (!response.ok) {
      const errorData = (await response
        .json()
        .catch(() => ({}))) as ErrorResponse;
      // This controller still renders the legacy bare `{ error: "msg" }` shape
      // on every action, while the /api/v1 standard is the `{ error: { message,
      // code } }` envelope. Read either, so migrating the controller to
      // StandardResponses later does not turn every message into
      // "[object Object]" for clients pinned to an older bundle.
      const field = errorData.error as string | { message?: string } | undefined;
      const message =
        (typeof field === "string" ? field : field?.message) ||
        response.statusText;
      throw new Error(`Failed to ${action}: ${response.status} ${message}`);
    }
    return (await response.json()) as T;
  }

  /**
   * Get all preferences with defaults for unset values
   */
  async getAll(): Promise<Record<string, PreferenceValue>> {
    // GET is idempotent — safe to retry transient network failures (ENG-10006).
    const response = await fetchWithNetworkRetry(
      this.baseUrl,
      {
        method: "GET",
        credentials: 'same-origin',
        headers: this.buildHeaders(),
      },
      { label: "UserPreferencesApi.getAll" }
    );

    const data = await this.handleResponse<PreferencesResponse>(response, "fetch preferences");
    return data.preferences;
  }

  /**
   * Get a specific preference by namespace
   * @param namespace - The preference namespace (e.g., "takeoff.shortcuts")
   */
  async get(namespace: string): Promise<PreferenceValue> {
    // GET is idempotent — safe to retry transient network failures (ENG-10006).
    const response = await fetchWithNetworkRetry(
      `${this.baseUrl}/${namespace}`,
      {
        method: "GET",
        credentials: 'same-origin',
        headers: this.buildHeaders(),
      },
      { label: `UserPreferencesApi.get ${namespace}` }
    );

    const data = await this.handleResponse<PreferenceResponse>(response, "fetch preference");
    return data.value;
  }

  /**
   * Set a specific preference value
   * @param namespace - The preference namespace
   * @param value - The preference value
   */
  async set(
    namespace: string,
    value: PreferenceValue
  ): Promise<PreferenceValue> {
    const response = await fetch(`${this.baseUrl}/${namespace}`, {
      method: "PUT",
      credentials: 'same-origin',
      headers: this.buildHeaders(),
      body: JSON.stringify({ value }),
    });

    const data = await this.handleResponse<PreferenceResponse>(response, "set preference");
    return data.value;
  }

  /**
   * Merge a partial value over a namespace, preserving keys you don't send.
   *
   * Prefer this over get() + set() when you only own part of a namespace: the
   * server does the read, merge and write under a single row lock, so two
   * clients editing different keys can't drop each other's write. A caller-side
   * get + set reads outside that lock and reopens the race.
   *
   * @param namespace - The preference namespace
   * @param partial - Keys to merge over the current value
   */
  async merge(
    namespace: string,
    partial: PreferenceValue
  ): Promise<PreferenceValue> {
    const response = await fetch(`${this.baseUrl}/${namespace}`, {
      method: "PATCH",
      credentials: 'same-origin',
      headers: this.buildHeaders(),
      body: JSON.stringify({ value: partial }),
    });

    const data = await this.handleResponse<PreferenceResponse>(response, "merge preference");
    return data.value;
  }

  /**
   * Reset a preference to its default value
   * @param namespace - The preference namespace
   */
  async reset(namespace: string): Promise<PreferenceValue> {
    const response = await fetch(`${this.baseUrl}/${namespace}`, {
      method: "DELETE",
      credentials: 'same-origin',
      headers: this.buildHeaders(),
    });

    const data = await this.handleResponse<PreferenceResponse>(response, "reset preference");
    return data.value;
  }

  /**
   * Update multiple preferences atomically
   * @param updates - Hash of namespace => value pairs
   */
  async bulkUpdate(
    updates: Record<string, PreferenceValue>
  ): Promise<Record<string, PreferenceValue>> {
    const response = await fetch(`${this.baseUrl}/bulk_update`, {
      method: "PUT",
      credentials: 'same-origin',
      headers: this.buildHeaders(),
      body: JSON.stringify({ updates }),
    });

    const data = await this.handleResponse<PreferencesResponse>(response, "bulk update preferences");
    return data.preferences;
  }

  /**
   * Get all default values
   */
  async getDefaults(): Promise<Record<string, PreferenceValue>> {
    // GET is idempotent — safe to retry transient network failures (ENG-10006).
    const response = await fetchWithNetworkRetry(
      `${this.baseUrl}/defaults`,
      {
        method: "GET",
        credentials: 'same-origin',
        headers: this.buildHeaders(),
      },
      { label: "UserPreferencesApi.getDefaults" }
    );

    const data = await this.handleResponse<DefaultsResponse>(response, "fetch defaults");
    return data.defaults;
  }

  /**
   * Build a request object suitable for a keepalive fetch during page unload.
   * Returns the URL, headers, and stringified body needed for a direct
   * fetch({ keepalive: true }) call, bypassing the normal response handling.
   */
  buildKeepaliveRequest(
    namespace: string,
    value: PreferenceValue,
    method: "PUT" | "PATCH" = "PUT",
  ): { url: string; headers: Record<string, string>; body: string; method?: "PATCH" } | null {
    let body: string;
    try {
      body = JSON.stringify({ value });
    } catch {
      return null;
    }
    return {
      url: `${this.baseUrl}/${namespace}`,
      headers: this.buildHeaders(),
      body,
      ...(method === "PATCH" && { method }),
    };
  }

  /**
   * Get all JSON schemas
   */
  async getSchemas(): Promise<Record<string, JSONSchema>> {
    // GET is idempotent — safe to retry transient network failures (ENG-10006).
    const response = await fetchWithNetworkRetry(
      `${this.baseUrl}/schemas`,
      {
        method: "GET",
        credentials: 'same-origin',
        headers: this.buildHeaders(),
      },
      { label: "UserPreferencesApi.getSchemas" }
    );

    const data = await this.handleResponse<SchemasResponse>(response, "fetch schemas");
    return data.schemas;
  }
}

// Singleton instance
export const userPreferencesApi = new UserPreferencesApi();

// Convenience functions
export const getAllPreferences = () => userPreferencesApi.getAll();
export const getPreference = (namespace: string) =>
  userPreferencesApi.get(namespace);
export const setPreference = (namespace: string, value: PreferenceValue) =>
  userPreferencesApi.set(namespace, value);
export const resetPreference = (namespace: string) =>
  userPreferencesApi.reset(namespace);
export const bulkUpdatePreferences = (
  updates: Record<string, PreferenceValue>
) => userPreferencesApi.bulkUpdate(updates);
export const getPreferenceDefaults = () => userPreferencesApi.getDefaults();
export const getPreferenceSchemas = () => userPreferencesApi.getSchemas();
